import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth.service';
import * as pdfjs from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const HelpAndSupport: React.FC = () => {
  const navigate = useNavigate();
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Help & Support - MRS';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isCancelled = false;
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;

    const renderPdf = async () => {
      if (!pdfContainerRef.current) {
        return;
      }

      try {
        setIsPdfLoading(true);
        setPdfError(null);

        const container = pdfContainerRef.current;
        container.innerHTML = '';

        const loadingTask = pdfjs.getDocument('/docs/user-manual.pdf');
        const pdf = await loadingTask.promise;
        const containerParent = container.parentElement;

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (isCancelled || !pdfContainerRef.current) {
            return;
          }

          const page = await pdf.getPage(pageNumber);

          const baseViewport = page.getViewport({ scale: 1 });
          const containerWidth = containerParent?.clientWidth ?? pdfContainerRef.current.clientWidth;
          const horizontalPadding = 32;
          const targetWidth = Math.max(containerWidth - horizontalPadding, 240);
          const scale = targetWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });
          const deviceScale = window.devicePixelRatio || 1;
          const isSmallScreen = window.innerWidth < 768;
          const outputScale = isSmallScreen ? 1 : Math.min(deviceScale, 2);

          const pageWrapper = document.createElement('div');
          pageWrapper.className = 'mb-4 flex justify-center';

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('Unable to initialize PDF canvas context.');
          }

          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.className = 'max-w-full h-auto bg-white rounded border border-gray-200 shadow-sm';

          pageWrapper.appendChild(canvas);
          container.appendChild(pageWrapper);

          await page.render({
            canvasContext: context,
            viewport,
            transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
          }).promise;
        }

        if (!isCancelled) {
          setIsPdfLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          setPdfError('Unable to load the user manual right now.');
          setIsPdfLoading(false);
        }
      }
    };

    renderPdf();

    const handleResize = () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }

      resizeTimer = setTimeout(() => {
        if (!isCancelled) {
          renderPdf();
        }
      }, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isCancelled = true;
      window.removeEventListener('resize', handleResize);

      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, []);

  const isLoggedIn = AuthService.isAuthenticated();

  const handleBack = () => {
    if (isLoggedIn) {
      navigate(-1);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/images/logo-uor.png" alt="University of Ruhuna" className="h-10 w-10 object-contain" />
            <img src="/images/logo-eng.png" alt="Faculty of Engineering" className="h-10 w-10 object-contain" />
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900">University of Ruhuna</p>
              <p className="text-xs text-gray-500">Faculty of Engineering</p>
            </div>
          </div>
          <button
            onClick={handleBack}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 bg-transparent border-0 cursor-pointer"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* Title Block */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8 text-center">
          <div className="flex justify-center items-center space-x-6 mb-6">
            <img src="/images/logo-uor.png" alt="University of Ruhuna" className="h-20 w-20 object-contain" />
            <img src="/images/logo-eng.png" alt="Faculty of Engineering" className="h-20 w-20 object-contain" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Help & Support</h1>
          <p className="text-lg text-blue-700 font-medium">Maintenance, Requests & Complaint Management System</p>
          <p className="text-base text-gray-600 mt-1">Faculty of Engineering - University of Ruhuna</p>
        </div>

        {/* Video Tutorial Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-100 rounded-lg p-2">
              <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Video Tutorial</h2>
              <p className="text-sm text-gray-500">Watch this tutorial to learn how to use the system</p>
            </div>
          </div>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/Dl2KKHqZ56w"
              title="MRS System Tutorial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* User Manual PDF Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 rounded-lg p-2">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">User Manual</h2>
              <p className="text-sm text-gray-500">Read the complete user manual for detailed instructions</p>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ height: '800px' }}>
            <div className="h-full overflow-y-auto bg-gray-50 p-4 relative">
              {isPdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-600 bg-gray-50/85 z-10">
                  Loading user manual...
                </div>
              )}

              {pdfError && (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <p className="text-sm text-red-600 mb-2">{pdfError}</p>
                  <a href="/docs/user-manual.pdf" className="text-blue-600 hover:underline text-sm" target="_blank" rel="noreferrer">
                    Open or download the PDF
                  </a>
                </div>
              )}

              <div ref={pdfContainerRef} />
            </div>
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-100 rounded-lg p-2">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Need More Help?</h2>
              <p className="text-sm text-gray-500">Contact our support team for additional assistance</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Email Support</p>
              <a href="mailto:support@eng.ruh.ac.lk" className="text-blue-600 hover:underline text-sm">
                support@eng.ruh.ac.lk
              </a>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Developed By</p>
              <a href="https://scorpion-xweb.vercel.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                ScorpionX
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 py-6">
          <p>Maintenance, Requests & Complaint Management System v1.0.0</p>
          <p>University of Ruhuna - Faculty of Engineering</p>
        </div>
      </div>
    </div>
  );
};

export default HelpAndSupport;
